// Formateo de importes con los decimales que REALMENTE tiene cada moneda.
//
// El catálogo pasó de 14 a 166 monedas, y forzar `minimumFractionDigits: 2`
// pintaba importes que no existen: el yen no tiene subunidad (¥1.560,25) y el
// dinar kuwaití tiene tres decimales (1 KWD = 1000 fils), no dos.
//
// La fuente preferida es `decimalPlaces` del backend (columna
// currency.decimal_places, editable por moneda). Si no llega, se deduce del
// código ISO con los datos CLDR del navegador. El modo de redondeo es el de
// Intl, HALF_UP, igual que en el backend.

const FALLBACK_DIGITS = 2;

/** Decimales de la moneda según su código ISO 4217 (JPY→0, USD→2, KWD→3). */
export function currencyFractionDigits(currencyCode?: string | null): number {
  if (!currencyCode) return FALLBACK_DIGITS;
  try {
    // maximumFractionDigits es opcional en el tipo de resolvedOptions(), aunque
    // con style:'currency' siempre viene resuelto.
    return (
      new Intl.NumberFormat('es-PE', {
        style: 'currency',
        currency: currencyCode,
      }).resolvedOptions().maximumFractionDigits ?? FALLBACK_DIGITS
    );
  } catch {
    // Código no reconocido (o un símbolo colado donde iba el código).
    return FALLBACK_DIGITS;
  }
}

/**
 * Importe formateado con su símbolo delante y los decimales de su moneda.
 *
 * @param price   importe
 * @param symbol  prefijo del catálogo (currency.prefix); si falta se usa el código
 * @param currencyCode código ISO, para saber cuántos decimales lleva
 * @param decimalPlaces decimales explícitos del backend, si se tienen a mano
 */
export function formatMoney(
  price?: number | null,
  symbol?: string | null,
  currencyCode?: string | null,
  decimalPlaces?: number | null,
): string {
  if (price === undefined || price === null) return '-';

  const digits = decimalPlaces ?? currencyFractionDigits(currencyCode);
  const num = new Intl.NumberFormat('es-PE', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(price);

  const label = symbol || currencyCode;
  return label ? `${label} ${num}` : num;
}
